import { Search } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';

/**
 * A labelled group of available-item cards. `key` must be unique among the
 * groups (labels need not be); each item should carry a React key unique within
 * its group, since the same item may legitimately appear in several groups.
 */
export type DecarbItemGroup = {
  key: string;
  label: string;
  items: JSX.Element[];
};

const AVAILABLE_ITEM_CELL_SIZE = { xs: 6, sm: 6, md: 4, lg: 3, xl: 2.4 };

type Props = {
  availableItems?: JSX.Element[];
  /**
   * When provided, the available column renders these groups — a heading per
   * group, followed by its cards — instead of the flat `availableItems` grid.
   * Leave undefined for the flat grid.
   */
  availableGroups?: DecarbItemGroup[];
  selectedItems: JSX.Element[];
  helperText?: React.ReactNode;
  overviewHeader?: React.ReactNode;

  searchComponent?: React.ReactNode;
  filterComponent?: React.ReactNode;
  sortComponent?: React.ReactNode;
};

export const DecarbContentLayout: React.FC<Props> = ({
  availableItems,
  availableGroups,
  selectedItems,
  overviewHeader,
  helperText,

  searchComponent,
  filterComponent,
  sortComponent,
}) => {
  return (
    <Paper
      sx={{ height: '100%', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
    >
      <Stack px={2} py={4} flexGrow={1} height={'100%'}>
        <Grid container height={'100%'}>
          <Grid size={6.5} height={'100%'}>
            <Stack height={'100%'} minHeight={0}>
              {/** QUERY / FILTER BUTTONS */}
              <Stack
                pr={6}
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                fontSize={{ xs: 20, sm: 24, md: 28, lg: 32, xl: 36 }}
              >
                <ButtonBase sx={{ fontSize: 'inherit' }}>
                  {searchComponent ? (
                    searchComponent
                  ) : (
                    <Search
                      sx={{
                        color: 'primary.main',
                        backgroundColor: 'success.main',
                        borderRadius: 1,
                        p: 1,
                        fontSize: 'inherit',
                      }}
                    />
                  )}
                </ButtonBase>
                <Stack direction={'row'} spacing={2}>
                  {filterComponent && (
                    <ButtonBase sx={{ fontSize: 'inherit' }}>
                      {filterComponent}
                    </ButtonBase>
                  )}
                  {sortComponent && (
                    <ButtonBase sx={{ fontSize: 'inherit' }}>
                      {sortComponent}
                    </ButtonBase>
                  )}
                </Stack>
              </Stack>

              <Box
                py={1}
                flexGrow={1}
                minHeight={0}
                pl={1}
                pr={4}
                pb={4}
                sx={{ overflowY: 'scroll', pt: '10px' }}
              >
                <Grid container rowSpacing={3} columnSpacing={1.5}>
                  {availableGroups
                    ? availableGroups.map((group) => (
                        <Grid
                          size={12}
                          key={group.key}
                          role='group'
                          aria-label={group.label}
                        >
                          <Typography
                            variant='overline'
                            component='h3'
                            color='text.secondary'
                          >
                            {group.label}
                          </Typography>
                          <Grid container rowSpacing={3} columnSpacing={1.5}>
                            {group.items.map((item, idx) => (
                              <Grid
                                size={AVAILABLE_ITEM_CELL_SIZE}
                                key={item.key ?? idx}
                              >
                                {item}
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      ))
                    : availableItems &&
                      availableItems.map((item, idx) => (
                        <Grid size={AVAILABLE_ITEM_CELL_SIZE} key={idx}>
                          {item}
                        </Grid>
                      ))}
                </Grid>
              </Box>
            </Stack>
          </Grid>

          <Grid size={5.5} height={'100%'}>
            <Stack height={'100%'} divider={<Divider />} spacing={4} px={4}>
              {/** OVERVIEW HEADER */}
              <Stack direction={'row'}>
                {overviewHeader && overviewHeader}
              </Stack>
              <Box
                py={1}
                flexGrow={1}
                minHeight={0}
                pl={1}
                pr={4}
                sx={{ overflowY: 'scroll', pt: '10px' }}
              >
                <Grid container rowSpacing={3} columnSpacing={1.5}>
                  {selectedItems.length > 0 &&
                    selectedItems.map((item, idx) => (
                      <Grid
                        size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                        key={idx}
                      >
                        {item}
                      </Grid>
                    ))}
                  {selectedItems.length === 0 && helperText}
                </Grid>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};
